import { useEffect, useMemo, useState } from 'react'
import {
  X, Loader2, Trash2, CalendarClock, StickyNote, Fingerprint, AlertTriangle,
  Ban, Users, Stethoscope, RotateCcw,
} from 'lucide-react'
import { postJson, patchJson, apiDelete, cachedGet } from '../../lib/api.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import SearchableSelect from '../SearchableSelect.jsx'
import ShiftBlocks from './ShiftBlocks.jsx'
import ShiftAssistants from './ShiftAssistants.jsx'
import {
  indexAppointments, specChoices, exceptChoices, pruneExcept, specLabel, exceptIds as doctorExceptIds,
} from '../../lib/dentist.js'
import {
  blankShift, shiftToForm, shiftPayload, changedFields, doctorTitleOf, durationMin,
  fmtDuration, fmtDayTH, fmtDateTH, timeOnDay, relabelBranch, branchWindow, findBranch,
  isShiftRow, rowDayKey,
} from '../../lib/doctorSchedule.js'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 disabled:bg-slate-50 disabled:text-slate-400'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

function SectionHead({ icon: Icon, title, note, right }) {
  return (
    <div className="flex items-center gap-2 mt-6 mb-2.5">
      <Icon size={15} className="text-brand-600" strokeWidth={1.9} />
      <h3 className="text-[13.5px] font-semibold text-slate-700">{title}</h3>
      {note && <span className="text-[11.5px] text-slate-400">{note}</span>}
      {right && <div className="ml-auto shrink-0">{right}</div>}
    </div>
  )
}

function TagRow({ items, tone = 'brand', onRemove, empty }) {
  if (!items?.length) return empty ? <div className="text-[12.5px] text-slate-400 pt-1">{empty}</div> : null
  const cls = tone === 'amber' ? 'bg-amber-50 text-amber-700' : 'bg-brand-50 text-brand-700'
  return (
    <div className="flex flex-wrap gap-1 pt-1.5">
      {items.map((t) => (
        <span key={t.key} className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[12px] ${cls}`}>
          {t.label}
          {onRemove && <button type="button" onClick={() => onRemove(t.key)} className="opacity-60 hover:opacity-100"><X size={11} /></button>}
        </span>
      ))}
    </div>
  )
}

// The list endpoints resolve creator/modified_by through an addon that outputs
// only `code`; a bare id is the honest fallback when it cannot.
function actorLabel(actor, id) {
  if (actor?.code) return actor.code
  const n = Number(id)
  return Number.isFinite(n) && n > 0 ? `#${n}` : '—'
}

/**
 * Drawer for one doctor shift. `row` is an `appointment` row with
 * `event_type = 'inverse-background'` (see lib/doctorSchedule.js for why the
 * roster lives there); pass `row = null` + `dayKey` to create one.
 *
 * `event_type` is deliberately NOT editable: it is the flag that makes the row a
 * shift at all, so a control for it could only ever break the row — either
 * hiding it from every roster feed or turning it into a phantom appointment.
 *
 * Four things are decided here and nowhere else:
 *
 *  1. **One shift per doctor per day per branch.** The doctor picker hides
 *     anyone the loaded month already rosters on this date+branch, so the
 *     duplicate cannot be typed in the first place.
 *  2. **The window comes from the branch**, not from a constant: clock-in + 45
 *     minutes → clock-out (lib/doctorSchedule.js#branchWindow). There is no
 *     all-day mode and no colour picker — a shift is a window, and the colour
 *     was decoration nobody read.
 *  3. **custom_spec / custom_except** — the per-shift override of the doctor's
 *     own spec/except, which every booking filter honours
 *     (lib/doctorFilter.js#shiftEligible). The two are written together or not
 *     at all.
 *  4. Breaks (`block` rows) and assistant links (`employee_timetable` rows) are
 *     OTHER rows, so those two sections save on their own the moment they are
 *     touched, and are offered only once this shift exists.
 */
export default function DoctorShiftEditor({
  row, dayKey, branchId, doctorOptions = [], doctorById, branches = [], monthRows = [],
  onClose, onSaved, onCreated,
}) {
  const isNew = !row?.id
  const [form, setForm] = useState(() => (
    isNew ? blankShift(dayKey, { branchId, branch: findBranch(branches, branchId) }) : shiftToForm(row)
  ))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [appt, setAppt] = useState({ rows: [], byId: new Map(), byDetail: new Map() })

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !confirmDelete) onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, confirmDelete])

  // The appointmentList catalog drives BOTH override pickers: `custom_spec`
  // holds its `type` strings and `custom_except` its row ids.
  useEffect(() => {
    cachedGet('/api/appointment-types')
      .then((r) => setAppt(indexAppointments(r))).catch(() => {})
  }, [])

  const branchOptions = useMemo(
    () => branches.map((b) => ({ id: String(b.id), name: relabelBranch(b.name) })),
    [branches],
  )

  /* ── one shift per doctor, per day, per branch ────────────────────────────
   * Whoever the loaded month already rosters on this date+branch is dropped
   * from the picker — a second window for the same doctor on the same day is
   * always a mistake, and the booking day view would draw the two stacked.
   * Only the loaded month can be checked, so switching the drawer to a branch
   * the page has not loaded simply stops excluding (and says so). */
  const takenDoctorIds = useMemo(() => {
    const taken = new Set()
    for (const r of monthRows) {
      if (!isShiftRow(r)) continue
      if (Number(r.id) === Number(row?.id)) continue
      if (rowDayKey(r) !== form.date) continue
      if (String(r.branch_id) !== String(form.branch_id)) continue
      taken.add(String(r.doctor_id))
    }
    return taken
  }, [monthRows, row?.id, form.date, form.branch_id])

  const loadedBranch = String(form.branch_id) === String(branchId)
  const pickableDoctors = useMemo(
    () => doctorOptions.filter((o) => !takenDoctorIds.has(String(o.value)) || String(o.value) === String(form.doctor_id)),
    [doctorOptions, takenDoctorIds, form.doctor_id],
  )
  const hiddenCount = doctorOptions.length - pickableDoctors.length

  const doctor = doctorById?.get?.(Number(form.doctor_id)) || null

  /* ── the branch's own hours ───────────────────────────────────────────────
   * Re-seeded when the branch changes, but only while the operator has not
   * typed a window of their own — a deliberate 13:00 must survive a branch
   * correction. Never on an existing row: those carry the window they were
   * rostered with. */
  const branchRec = useMemo(() => findBranch(branches, form.branch_id), [branches, form.branch_id])
  const defaultWindow = useMemo(() => branchWindow(branchRec), [branchRec])
  useEffect(() => {
    if (!isNew) return
    setForm((f) => {
      const w = branchWindow(findBranch(branches, f.branch_id))
      const untouched = !f._touchedTime
      return untouched ? { ...f, start: w.start, end: w.end } : f
    })
  }, [form.branch_id, branches, isNew])

  const atDefault = form.start === defaultWindow.start && form.end === defaultWindow.end

  // Live readout of the window being composed — the same arithmetic the payload
  // will do, so what the footer saves is what this line already said.
  const preview = useMemo(
    () => durationMin(timeOnDay(form.date, form.start), timeOnDay(form.date, form.end)),
    [form.date, form.start, form.end],
  )

  /* ── per-shift spec / except ─────────────────────────────────────────────
   * OFF = this shift inherits the doctor's record. ON = these two lists replace
   * it for this shift only. Turning it on seeds from the doctor's defaults, so
   * the operator edits what is already true rather than starting from nothing. */
  const docSpec = useMemo(
    () => (Array.isArray(doctor?.spec) ? doctor.spec.filter(Boolean).map(String) : []),
    [doctor],
  )
  const docExcept = useMemo(() => doctorExceptIds(doctor, appt.byDetail), [doctor, appt.byDetail])

  function toggleCustom() {
    setForm((f) => (f.custom_on
      ? { ...f, custom_on: false }
      : { ...f, custom_on: true, custom_spec: f.custom_spec.length ? f.custom_spec : docSpec, custom_except: f.custom_spec.length ? f.custom_except : docExcept }))
  }

  const specSel = useMemo(() => new Set(form.custom_spec), [form.custom_spec])
  const specOpts = useMemo(() => specChoices(appt.rows, doctor), [appt.rows, doctor])
  const exceptOpts = useMemo(() => exceptChoices(appt.rows, specSel), [appt.rows, specSel])

  function setSpec(next) {
    const spec = [...next]
    setForm((f) => ({ ...f, custom_spec: spec, custom_except: pruneExcept(f.custom_except, appt.byId, next) }))
  }

  // What the booking screens will actually filter this shift by — the override
  // when it is on, the doctor's record when it is not. The assistant matcher
  // reads exactly this, so the two sections can never disagree.
  const effSpec = useMemo(
    () => new Set(form.custom_on ? form.custom_spec : docSpec),
    [form.custom_on, form.custom_spec, docSpec],
  )
  const effExcept = useMemo(
    () => new Set((form.custom_on ? form.custom_except : docExcept).map(Number)),
    [form.custom_on, form.custom_except, docExcept],
  )

  // Moving an existing shift does not move whoever is already booked inside it —
  // say so before it is saved, not after someone notices.
  const moved = !isNew && (
    String(form.doctor_id) !== String(row.doctor_id)
    || String(form.branch_id) !== String(row.branch_id)
    || form.date !== String(row.date || '').slice(0, 10)
  )

  async function save() {
    // `titleDoctorTB` is Bubble's denormalised doctor label. The doctor list is
    // active-only, so an inactive doctor resolves to nothing — falling back to
    // what the row already carries keeps an unrelated edit from blanking it.
    // The fallback is dropped the moment the shift is moved to another doctor.
    const sameDoctor = !isNew && String(form.doctor_id) === String(row.doctor_id)
    const doctorTitle = doctorTitleOf(doctor) || (sameDoctor ? (row.titleDoctorTB || '') : '')
    const { payload, error } = shiftPayload(form, { doctorTitle })
    if (error) { setErr(error); return }
    setSaving(true); setErr('')
    try {
      if (isNew) {
        const created = await postJson('/api/hr/schedule/doctor', payload)
        // Breaks and assistants attach to a shift that EXISTS, so a fresh shift
        // stays open on its own record instead of closing and making the
        // operator find it again to add either.
        if (created?.id && onCreated) { onCreated(created); return }
        onSaved?.()
      } else {
        const patch = changedFields(payload, row)
        // Nothing moved — closing quietly beats a PATCH that only stamps
        // modified_at and fires a realtime event at every open booking screen.
        if (Object.keys(patch).length) await patchJson(`/api/hr/schedule/doctor/${row.id}`, patch)
        onSaved?.()
      }
    } catch (e) { setErr(String(e.message || e)) }
    finally { setSaving(false) }
  }

  async function remove() {
    setConfirmDelete(false)
    setSaving(true); setErr('')
    try {
      // The proxy also takes this shift's ห้ามแทรก breaks with it, and reports
      // any it could not remove — passed up, because the drawer is about to close.
      const res = await apiDelete(`/api/hr/schedule/doctor/${row.id}`)
      onSaved?.(res)
    } catch (e) { setErr(String(e.message || e)) }
    finally { setSaving(false) }
  }

  const title = isNew ? 'เพิ่มเวรทันตแพทย์' : (doctorTitleOf(doctor) || row?.titleDoctorTB || 'แก้ไขเวรทันตแพทย์')

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-[620px] bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-slate-900 truncate">{title}</h2>
            <p className="text-[12.5px] text-slate-500">{fmtDayTH(form.date)}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <SectionHead icon={CalendarClock} title="เวรออกตรวจ" />

          <div className="space-y-3">
            <div>
              <label className={LABEL}>ทันตแพทย์</label>
              <SearchableSelect
                options={pickableDoctors}
                value={String(form.doctor_id || '')}
                onChange={(v) => set({ doctor_id: v })}
                placeholder="เลือกทันตแพทย์"
                searchPlaceholder="พิมพ์ชื่อ / ชื่อเล่นหมอ…"
                emptyText="ทันตแพทย์ทุกท่านมีเวรในวันนี้แล้ว"
              />
              {loadedBranch && hiddenCount > 0 && (
                <p className="mt-1 text-[11.5px] text-slate-400">ซ่อน {hiddenCount} ท่านที่มีเวรในวัน/สาขานี้แล้ว</p>
              )}
              {!loadedBranch && (
                <p className="mt-1 text-[11.5px] text-amber-600">สาขานี้ยังไม่ได้โหลดตาราง — ระบบยังตรวจเวรซ้ำให้ไม่ได้</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>สาขา</label>
                <select value={String(form.branch_id || '')} onChange={(e) => set({ branch_id: e.target.value })} className={FIELD}>
                  <option value="">เลือกสาขา</option>
                  {branchOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL}>วันที่</label>
                <input type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} className={FIELD} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>เวลาเริ่มเวร</label>
                <input type="time" value={form.start} onChange={(e) => set({ start: e.target.value, _touchedTime: true })} className={FIELD} />
              </div>
              <div>
                <label className={LABEL}>เวลาสิ้นสุด</label>
                <input type="time" value={form.end} onChange={(e) => set({ end: e.target.value, _touchedTime: true })} className={FIELD} />
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 text-[11.5px]">
              <span className="text-slate-400">
                ระยะเวลา <span className="font-medium text-slate-600">{fmtDuration(preview)}</span> · เวลาตามเขตเวลาไทย
              </span>
              {!atDefault && (
                <button type="button" onClick={() => set({ start: defaultWindow.start, end: defaultWindow.end, _touchedTime: false })}
                  className="inline-flex items-center gap-1 text-brand-600 hover:underline shrink-0">
                  <RotateCcw size={11} /> เวลามาตรฐานสาขา {defaultWindow.start}–{defaultWindow.end}
                </button>
              )}
            </div>
          </div>

          {moved && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-2.5 text-[12.5px] text-amber-800">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>ย้ายหมอ / สาขา / วันที่ของเวรนี้ <b>ไม่ได้ย้ายนัดคนไข้ที่จองไว้ในช่วงเวลาเดิม</b> — ต้องโยกเคสเองที่หน้านัดหมาย</span>
            </div>
          )}

          {/* ── per-shift spec / except ── */}
          <SectionHead icon={Stethoscope} title="เคสที่รับในเวรนี้"
            note={form.custom_on ? 'กำหนดเองเฉพาะเวรนี้' : 'ใช้ค่าเริ่มต้นของหมอ'}
            right={(
              <button type="button" onClick={toggleCustom} disabled={!form.doctor_id}
                className={`px-2.5 py-1.5 rounded-lg text-[12.5px] font-medium ring-1 transition disabled:opacity-40 ${
                  form.custom_on ? 'bg-brand-50 text-brand-700 ring-brand-200' : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'}`}>
                {form.custom_on ? 'กลับไปใช้ค่าเริ่มต้น' : 'กำหนดเอง'}
              </button>
            )} />

          {!form.doctor_id ? (
            <div className="text-[12.5px] text-slate-400">เลือกทันตแพทย์ก่อน</div>
          ) : !form.custom_on ? (
            <div className="rounded-xl ring-1 ring-slate-200 bg-slate-50/60 px-3 py-2.5">
              <div className="text-[12px] text-slate-400">ความเชี่ยวชาญ (จากประวัติหมอ)</div>
              <TagRow items={docSpec.map((s) => ({ key: s, label: specLabel(s) }))} empty="ไม่ระบุ" />
              {docExcept.length > 0 && (
                <>
                  <div className="text-[12px] text-slate-400 mt-2">ไม่รับเคส</div>
                  <TagRow tone="amber" items={docExcept.map((x) => ({ key: x, label: appt.byId.get(Number(x))?.detail || `#${x}` }))} />
                </>
              )}
              <p className="mt-2 text-[11.5px] text-slate-400">
                กด “กำหนดเอง” เมื่อเวรนี้รับ (หรือไม่รับ) ต่างจากปกติ — มีผลเฉพาะเวรนี้ และใช้กรองหมอในหน้านัดหมาย
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className={LABEL}>ความเชี่ยวชาญที่รับในเวรนี้ (custom_spec)</label>
                <SearchableSelect
                  multiple options={specOpts} values={specSel} onChange={setSpec}
                  placeholder="เลือกประเภทการรักษา…" searchPlaceholder="ค้นหาประเภทการรักษา…"
                />
                <TagRow
                  items={form.custom_spec.map((s) => ({ key: s, label: specLabel(s) }))}
                  onRemove={(k) => setSpec(new Set(form.custom_spec.filter((s) => s !== k)))}
                  empty="ยังไม่ได้เลือก — ต้องเลือกอย่างน้อย 1 รายการ"
                />
              </div>
              <div>
                <label className={LABEL}>ไม่รับเคสในเวรนี้ (custom_except)</label>
                <SearchableSelect
                  multiple options={exceptOpts} values={new Set(form.custom_except)}
                  onChange={(next) => set({ custom_except: [...next] })}
                  disabled={!form.custom_spec.length}
                  placeholder={form.custom_spec.length ? 'เลือกเคสที่ไม่รับ…' : 'เลือกความเชี่ยวชาญก่อน'}
                  searchPlaceholder="ค้นหาเคส…"
                  emptyText="ไม่มีเคสในความเชี่ยวชาญที่เลือก"
                />
                <TagRow
                  tone="amber"
                  items={form.custom_except.map((x) => ({ key: x, label: appt.byId.get(Number(x))?.detail || `#${x}` }))}
                  onRemove={(k) => set({ custom_except: form.custom_except.filter((x) => x !== k) })}
                  empty="รับทุกเคสในความเชี่ยวชาญที่เลือก"
                />
              </div>
              <p className="text-[11.5px] text-slate-400">
                ค่านี้จะ <b>แทนที่</b> spec/except เดิมของหมอสำหรับเวรนี้ทั้งหมด (ไม่ใช่เพิ่มเข้าไป) — มีผลกับการค้นหา/กรองหมอในหน้านัดหมายและทั้งระบบ
              </p>
            </div>
          )}

          <SectionHead icon={StickyNote} title="รายละเอียด" />
          <textarea rows={2} value={form.details} onChange={(e) => set({ details: e.target.value })}
            placeholder="เช่น ออกตรวจครึ่งวัน · เคสจัดฟันเท่านั้น…" className={FIELD} />

          {/* ── breaks + assistants: OTHER rows, saved on the spot ── */}
          <SectionHead icon={Ban} title="บล็อกเวลาห้ามแทรก" note="บันทึกทันทีเมื่อแก้ไข" />
          {isNew ? (
            <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2.5 text-[12.5px] text-slate-500">
              บันทึกเวรก่อน แล้วจึงเพิ่มบล็อกเวลาห้ามแทรกได้
            </div>
          ) : (
            <ShiftBlocks shift={row} startTime={form.start} endTime={form.end} onChanged={() => {}} />
          )}

          <SectionHead icon={Users} title="ผู้ช่วยประจำเวร" note="บันทึกทันทีเมื่อแก้ไข" />
          {isNew ? (
            <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2.5 text-[12.5px] text-slate-500">
              บันทึกเวรก่อน แล้วจึงผูกผู้ช่วยกับเวรนี้ได้
            </div>
          ) : (
            <ShiftAssistants
              shift={row} spec={effSpec} exceptIds={effExcept}
              appt={{ apptById: appt.byId, apptByDetail: appt.byDetail }}
              onChanged={() => {}}
            />
          )}

          {/* Provenance is reference, not content — one muted line at the foot of
              the drawer, where a four-row table was out-weighing the sections
              that are actually edited. */}
          {!isNew && (
            <div className="mt-6 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11.5px] text-slate-400">
              <Fingerprint size={12} className="shrink-0" />
              <span className="tabular-nums">appointment #{row.id}</span>
              <span>· สร้างโดย {actorLabel(row?._creator, row?.creator)}</span>
              <span>· แก้ไขล่าสุด {actorLabel(row?._modifier, row?.modified_by)} {fmtDateTH(row?.modified_at)}</span>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-6 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {!isNew && (
              <button onClick={() => setConfirmDelete(true)} disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                <Trash2 size={14} /> ลบเวร
              </button>
            )}
            <span className="text-[12.5px] text-rose-500 truncate">{err}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={onClose} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100">ปิด</button>
            <button onClick={save} disabled={saving}
              className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-1.5">
              {saving && <Loader2 size={14} className="animate-spin" />} บันทึก
            </button>
          </div>
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title="ลบเวรออกตรวจ"
          message="ระบบจะซ่อนเวรนี้ออกจากตาราง (เก็บประวัติไว้) พร้อมซ่อนบล็อกเวลาห้ามแทรกของหมอท่านนี้ในวัน/สาขาเดียวกัน และลบเวรผู้ช่วยที่ผูกกับเวรนี้ทิ้งด้วย · ถ้ามีนัดคนไข้ค้างอยู่ ระบบจะไม่ยอมให้ลบ"
          confirmLabel="ลบเวร"
          tone="danger"
          onConfirm={remove}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  )
}
